import { useApolloClient } from "@apollo/client";
import {
  Button,
  Callout,
  Dialog,
  DialogBody,
  DialogFooter,
  FormGroup,
  ProgressBar,
  TagInput,
  type Intent,
} from "@blueprintjs/core";
import { ALLOWED_MEDIA_TYPES } from "@package/ui-config";
import {
  CreateMediaDocument,
  GetMediaDocument,
  GetMediaGridDocument,
  GetMediaUploadUrlDocument,
  type CreateMediaMutation,
  type CreateMediaMutationVariables,
  type GetMediaUploadUrlQuery,
  type GetMediaUploadUrlQueryVariables,
} from "@package/ui-gql/index-data-access";
import { uploadMediaFile } from "@package/ui-rest";
import { produce } from "immer";
import { last } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";

type FileUploadStatus = {
  file: File;
  uploading?: boolean;
  complete?: boolean;
  error?: boolean;
};

type DialogUploadMediaProps = {
  isOpen: boolean;
  selectedParentIdHierarchy: string[];
  onClose: () => void;
};

function getIntentForFileUploadStatus(file?: FileUploadStatus): Intent {
  if (file?.error) {
    return "danger";
  }
  if (file?.complete) {
    return "success";
  }
  if (file?.uploading) {
    return "primary";
  }
  return "none";
}

export function DialogUploadMedia({
  isOpen,
  selectedParentIdHierarchy,
  onClose,
}: DialogUploadMediaProps) {
  const client = useApolloClient();

  const [files, setFiles] = useState<File[]>([]);
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const uploadAllFiles = useCallback(async () => {
    const fileFailed = (index: number) => {
      setError(true);
      setFileStatuses((prev) =>
        produce(prev, (draft) => {
          if (draft?.[index]) {
            draft[index].uploading = false;
            draft[index].error = true;
            draft[index].complete = true;
          }
        }),
      );
    };

    setUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      setFileStatuses((prev) =>
        produce(prev, (draft) => {
          if (draft?.[i]) {
            draft[i].uploading = true;
          }
        }),
      );

      const urlResult = await client.query<
        GetMediaUploadUrlQuery,
        GetMediaUploadUrlQueryVariables
      >({
        query: GetMediaUploadUrlDocument,
        variables: {
          fileName: file.name,
        },
      });
      const { fileName, url } = urlResult?.data?.item ?? {};

      if (!fileName || !url) {
        fileFailed(i);
        break;
      }

      await uploadMediaFile(file, url);

      try {
        const result = await client.mutate<
          CreateMediaMutation,
          CreateMediaMutationVariables
        >({
          mutation: CreateMediaDocument,
          variables: {
            file: fileName,
            isDir: false,
            name: file.name,
            parent: last(selectedParentIdHierarchy),
            size: file.size,
            type: file.type,
          },
        });

        if (result.errors?.length) {
          fileFailed(i);
          break;
        }
      } catch (e: unknown) {
        console.log(e);
        fileFailed(i);
        break;
      }

      setFileStatuses((prev) =>
        produce(prev, (draft) => {
          if (draft?.[i]) {
            draft[i].uploading = false;
            draft[i].complete = true;
          }
        }),
      );
    }

    await client.refetchQueries({
      include: [GetMediaGridDocument, GetMediaDocument],
    });

    setUploading(false);
    onClose();
  }, [client, files, onClose, selectedParentIdHierarchy]);

  useEffect(
    function effectOnFilesChanged() {
      setFileStatuses(files.map((file) => ({ file })));
    },
    [files],
  );

  useEffect(
    function effectOnIsOpenChanged() {
      setFiles([]);
      setFileStatuses([]);
      setError(false);
      setUploading(false);
    },
    [isOpen],
  );

  return (
    <Dialog
      icon="cloud-upload"
      isOpen={isOpen}
      onClose={() => onClose()}
      title="Upload Media Files"
    >
      <DialogBody>
        <FormGroup>
          <FileUploader
            disabled={uploading}
            handleChange={(value?: File | File[]) => {
              let files: File[] = [];

              if (value instanceof FileList) {
                files = Array.from(value);
              } else if (Array.isArray(value)) {
                files = value;
              } else if (value instanceof File) {
                files = [value];
              }

              setFiles((prev) =>
                produce(prev, (draft) => {
                  draft.push(...files);
                }),
              );
            }}
            multiple
            types={ALLOWED_MEDIA_TYPES}
            uploadedLabel="Staged successfully! Upload another?"
          />
        </FormGroup>
        <FormGroup>
          <ProgressBar
            animate={uploading}
            intent={uploading ? "primary" : "none"}
            stripes={uploading}
          />
        </FormGroup>
        {error && (
          <FormGroup>
            <Callout intent="danger" title="Failed Uploading Files">
              Failed uploading all the selected files. See below list of files
              for which one failed.
            </Callout>
          </FormGroup>
        )}
        <FormGroup>
          <TagInput
            autoResize
            disabled={uploading || !files.length}
            fill
            intent="none"
            onRemove={(_, index) => {
              setFiles((prev) =>
                produce(prev, (draft) => {
                  draft.splice(index, 1);
                }),
              );
            }}
            placeholder="No files staged for upload"
            tagProps={(_, index) => ({
              intent: getIntentForFileUploadStatus(fileStatuses[index]),
              fill: true,
            })}
            values={fileStatuses.map((file) => file.file.name)}
          />
        </FormGroup>
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button
              intent="none"
              disabled={uploading}
              text="Cancel"
              onClick={() => onClose()}
            />
            <Button
              className="ml-2"
              intent="primary"
              text="Upload"
              disabled={uploading}
              onClick={() => {
                uploadAllFiles();
              }}
            />
          </>
        }
      />
    </Dialog>
  );
}
